import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export class NameValidator {
  /**
   * Pattern for valid names:
   * - Only letters (a-z, A-Z) and spaces
   * - Minimum 2 characters
   * - No special characters, numbers, or symbols
   * - Must contain at least some letters (not just spaces)
   */
  static readonly NAME_PATTERN = /^[a-zA-Z]+(?:\s[a-zA-Z]+)*$/;

  /**
   * Validator function for standard name format
   * Requires at least 2 characters, only letters and spaces, no special characters
   * @param control - The form control to validate
   * @returns ValidationErrors | null
   */
  static standardName(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const name = control.value.toString().trim();
    
    // Check minimum length
    if (name.length < 2) {
      return { 
        standardName: { 
          value: control.value,
          message: 'Name must be at least 2 characters long'
        } 
      };
    }

    // Check if it contains only letters and spaces (more permissive pattern)
    if (!/^[a-zA-Z\s]+$/.test(name)) {
      return { 
        standardName: { 
          value: control.value,
          message: 'Name can only contain letters and spaces'
        } 
      };
    }

    // Check if it's all spaces
    if (name.replace(/\s/g, '').length === 0) {
      return { 
        standardName: { 
          value: control.value,
          message: 'Name must contain at least some letters'
        } 
      };
    }

    return null; // Valid name
  }

  /**
   * Validator function for single word names (first name, last name)
   * Requires at least 2 characters, only letters, no spaces or special characters
   * @param control - The form control to validate
   * @returns ValidationErrors | null
   */
  static singleWordName(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const name = control.value.toString().trim();
    
    // Check minimum length
    if (name.length < 2) {
      return { 
        singleWordName: { 
          value: control.value,
          message: 'Name must be at least 2 characters long'
        } 
      };
    }

    // Check if it contains only letters (no spaces allowed for single word names)
    if (!/^[a-zA-Z]+$/.test(name)) {
      return { 
        singleWordName: { 
          value: control.value,
          message: 'Name can only contain letters'
        } 
      };
    }

    return null; // Valid single word name
  }

  /**
   * Check if name is valid with standard format
   * @param name - Name to validate
   * @returns boolean
   */
  static isValidStandardName(name: string): boolean {
    if (!name) return false;
    
    const control = { value: name } as AbstractControl;
    return NameValidator.standardName(control) === null;
  }

  /**
   * Check if name is valid single word format
   * @param name - Name to validate
   * @returns boolean
   */
  static isValidSingleWordName(name: string): boolean {
    if (!name) return false;
    
    const control = { value: name } as AbstractControl;
    return NameValidator.singleWordName(control) === null;
  }

  /**
   * Format name to proper case (first letter of each word capitalized)
   * @param name - Raw name string
   * @returns Formatted name or original if invalid
   */
  static formatName(name: string): string {
    if (!name) return name;
    
    return name
      .trim()
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}

/**
 * Factory function to create standard name validator
 * @returns ValidatorFn
 */
export function standardNameValidator(): ValidatorFn {
  return NameValidator.standardName;
}

/**
 * Factory function to create single word name validator
 * @returns ValidatorFn
 */
export function singleWordNameValidator(): ValidatorFn {
  return NameValidator.singleWordName;
}
